<template>
  <v-container fluid>
    <v-card outlined :loading="loadingSettings">
      <template v-slot:progress>
        <v-progress-linear color="primary" indeterminate></v-progress-linear>
      </template>
      <validation-observer ref="form">
        <v-form @submit.prevent="onSubmit">
          <v-tabs grow>
            <v-tab>{{ $t('return.settings.globalSettings') }}</v-tab>
            <v-tab v-if="settingsForm.available_address_picking">{{ $t('return.settings.formSettings') }}</v-tab>
            <v-tab>{{ $t('return.settings.frontReturn') }}</v-tab>
            <v-tab>{{ $t('return.settings.displaySettings') }}</v-tab>
            <v-tab>{{ $t('return.settings.refundSection') }}</v-tab>
            <v-tab-item>
              <v-container fluid>
                <v-card outlined>
                  <v-card-title>{{ $t('return.settings.globalSettings') }}</v-card-title>
                  <v-container>
                    <validation-provider
                      v-slot="{ errors }"
                      :name="$t('return.settings.country')"
                      :rules="!settingsForm.default_settings ? 'required' : ''"
                    >
                      <CountriesAutocomplete
                        v-model="settingsForm.country_code"
                        :errorMessage="errors"
                        :required="true"
                        :disabled="!!$route.params.id"
                      />
                    </validation-provider>
                    <v-switch v-model="settingsForm.default_settings" :label="$t('return.settings.defaultSettings')" />
                    <v-row>
                      <v-col cols="11" sm="5">
                        <v-menu
                          ref="begin"
                          v-model="menuBegin"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          :return-value.sync="settingsForm.pickup_hours.begin"
                          max-width="290px"
                          min-width="290px"
                          offset-y
                          transition="scale-transition"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="settingsForm.pickup_hours.begin"
                              v-bind="attrs"
                              v-on="on"
                              :label="$t('return.settings.beginHours')"
                              append-icon="mdi-clock-time-four-outline"
                              readonly
                              clearable
                              :disabled="!settingsForm.available_address_picking"
                            ></v-text-field>
                          </template>
                          <v-time-picker
                            v-if="menuBegin"
                            v-model="settingsForm.pickup_hours.begin"
                            :allowed-minutes="allowedStep"
                            :max="settingsForm.pickup_hours.end"
                            format="24hr"
                            full-width
                            @click:minute="$refs.begin.save(settingsForm.pickup_hours.begin)"
                          ></v-time-picker>
                        </v-menu>
                      </v-col>
                      <v-spacer></v-spacer>
                      <v-col cols="11" sm="5">
                        <v-menu
                          ref="end"
                          v-model="menuEnd"
                          :close-on-content-click="false"
                          :nudge-right="40"
                          :return-value.sync="settingsForm.pickup_hours.end"
                          max-width="290px"
                          min-width="290px"
                          offset-y
                          transition="scale-transition"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="settingsForm.pickup_hours.end"
                              v-bind="attrs"
                              v-on="on"
                              :label="$t('return.settings.endHours')"
                              append-icon="mdi-clock-time-four-outline"
                              readonly
                              clearable
                              :disabled="!settingsForm.available_address_picking"
                            ></v-text-field>
                          </template>
                          <v-time-picker
                            v-if="menuEnd"
                            v-model="settingsForm.pickup_hours.end"
                            :allowed-minutes="allowedStep"
                            :min="settingsForm.pickup_hours.begin"
                            format="24hr"
                            full-width
                            @click:minute="$refs.end.save(settingsForm.pickup_hours.end)"
                          ></v-time-picker>
                        </v-menu>
                      </v-col>
                    </v-row>
                    <v-switch
                      v-model="settingsForm.available_address_picking"
                      :label="$t('return.settings.availablePicking')"
                    />
                    <v-switch
                      v-model="settingsForm.charge_return_fees.available"
                      :label="$t('return.settings.charge_return_fees.customerChargeReturnFees')"
                    ></v-switch>
                    <v-row v-if="settingsForm.charge_return_fees.available">
                      <v-col
                        cols="12"
                        :sm="settingsForm.charge_return_fees.prepaid_return_label ? '6' : '12'"
                        class="py-0"
                      >
                        <v-switch
                          class="pl-4"
                          v-model="settingsForm.charge_return_fees.prepaid_return_label"
                          :label="$t('return.settings.charge_return_fees.generatePrepaidReturnLabel')"
                        ></v-switch>
                      </v-col>
                      <v-col
                        cols="12"
                        sm="6"
                        class="py-0"
                        v-if="settingsForm.charge_return_fees.prepaid_return_label"
                        align-self="center"
                      >
                        <v-row>
                          <v-col cols="7" align-self="center" class="py-0">
                            <ValidationProvider
                              :rules="'min_value:0.01|required'"
                              name="return fees amount"
                              v-slot="{ errors }"
                            >
                              <v-text-field
                                :class="$vuetify.breakpoint.width < 600 ? 'ml-4' : ''"
                                dense
                                :label="$t('return.settings.charge_return_fees.returnFeesAmount')"
                                v-model="settingsForm.charge_return_fees.return_fees_amount"
                                type="number"
                                :error-messages="errors"
                              ></v-text-field>
                            </ValidationProvider>
                          </v-col>
                          <v-col cols="5" class="py-0">
                            <ValidationProvider rules="required" name="currency_code" v-slot="{ errors }">
                              <v-autocomplete
                                v-model="settingsForm.charge_return_fees.return_fees_currency"
                                :items="currencies"
                                :item-text="(item) => `${item.code} (${item.name})`"
                                :item-value="(item) => item"
                                :label="$t('return.settings.currency')"
                                class="d-flex justify-center align-center"
                                dense
                                hide-details="auto"
                                :error-messages="errors"
                                outlined
                              ></v-autocomplete>
                            </ValidationProvider>
                          </v-col>
                        </v-row>
                      </v-col>
                      <v-col class="py-0">
                        <v-switch
                          class="pl-4 pt-0"
                          v-model="settingsForm.charge_return_fees.display_button"
                          :label="$t('return.settings.charge_return_fees.chargeReturnFeesButton')"
                        ></v-switch>
                      </v-col>
                    </v-row>
                    <v-switch
                      v-model="settingsForm.refund_order_carrier_cost.activate"
                      :label="$t('return.settings.refundOrderCarrierCost')"
                    ></v-switch>
                    <v-switch
                      v-model="settingsForm.refund_order_carrier_cost.display_refund_button"
                      :label="$t('return.settings.displayRefundButton')"
                    ></v-switch>
                  </v-container>
                </v-card>
                <v-card outlined class="mt-3">
                  <v-card-title>{{ $t('return.settings.shopReturn') }}</v-card-title>
                  <v-container>
                    <v-row>
                      <v-col>
                        <v-switch
                          v-model="settingsForm.refund_on_terminal.activate"
                          :label="$t('return.settings.terminalRefund')"
                        />

                        <v-switch
                          class="ml-4"
                          v-if="isOctipasAdmin || vueAppEnv !== 'prod'"
                          v-model="settingsForm.refund_on_terminal.activate_test_mode"
                          :label="$t('return.settings.activateTestMode')"
                          :disabled="!settingsForm.refund_on_terminal.activate"
                        ></v-switch>
                        <v-switch
                          class="ml-4"
                          v-model="settingsForm.refund_on_terminal.send_return_reference"
                          :label="$t('return.settings.sendReturnReference')"
                          :disabled="!settingsForm.refund_on_terminal.activate"
                          :messages="$t('return.settings.sendReturnReferenceMessage')"
                        ></v-switch>
                      </v-col>
                    </v-row>
                    <v-row>
                      <v-col>
                        <v-select
                          v-if="isAdmin"
                          v-model="settingsForm.allow_return_creation_in_shop"
                          :items="itemReturnCreationAuthorizedRoles"
                          :label="$t('return.settings.shopReturnCreationAuthorizedRoles')"
                          multiple
                        >
                        </v-select>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>

                <v-card outlined class="mt-3">
                  <v-card-title>{{ $t('return.settings.cancelAfterReceipt.title') }}</v-card-title>
                  <v-container>
                    <validation-provider
                      v-slot="{ errors }"
                      :name="$t('return.settings.cancelAfterReceipt.behavior')"
                      :rules="{ required: true, one_of: cancelAfterReceiptBehaviors.map((behavior) => behavior.value) }"
                    >
                      <v-select
                        v-model="settingsForm.cancel_after_receipt.behavior"
                        :items="cancelAfterReceiptBehaviors"
                        :label="$t('return.settings.cancelAfterReceipt.behavior')"
                        :hint="$t('return.settings.cancelAfterReceipt.behaviorHelp')"
                        :error-messages="errors"
                        persistent-hint
                      />
                    </validation-provider>
                    <v-switch
                      v-model="settingsForm.cancel_after_receipt.ask_confirmation"
                      :label="$t('return.settings.cancelAfterReceipt.askConfirmation')"
                      :messages="$t('return.settings.cancelAfterReceipt.confirmationHelp')"
                    />
                  </v-container>
                </v-card>

                <v-card outlined class="mt-3">
                  <v-card-title>{{ $t('return.settings.returnDeadline') }}</v-card-title>
                  <v-container>
                    <v-row>
                      <v-col cols="12" lg="8" md="8">
                        <v-switch
                          v-model="settingsForm.waiting_products_receipt_timeout.activate"
                          :label="$t('return.settings.cancelWaitingReceptionReturn')"
                          :messages="$t('return.settings.cancelWaitingReceptionReturnMessage')"
                        />
                      </v-col>
                      <v-col cols="12" lg="4" md="4" class="d-flex align-center">
                        <validation-provider
                          rules="min_value:1|integer|required"
                          :name="$t('return.settings.cancelDelay')"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            hide-details="auto"
                            class="text-uppercase"
                            outlined
                            dense
                            v-model="settingsForm.waiting_products_receipt_timeout.delay"
                            type="number"
                            :label="$t('return.settings.cancelDelay')"
                            :error-messages="errors"
                            required
                            :disabled="loading || !settingsForm.waiting_products_receipt_timeout.activate"
                          ></v-text-field>
                        </validation-provider>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </v-container>
            </v-tab-item>
            <v-tab-item v-if="settingsForm.available_address_picking">
              <v-container fluid>
                <v-card outlined>
                  <v-card-title>{{ $t('return.settings.formSettings') }}</v-card-title>
                  <v-container>
                    <v-row>
                      <v-col
                        v-for="(field, propertyName) in settingsForm.optional_form_fields"
                        v-bind:key="propertyName"
                        col="12"
                        lg="6"
                        md="6"
                        sm="12"
                      >
                        <v-switch
                          v-model="settingsForm.optional_form_fields[propertyName]"
                          :label="$t('return.settings.' + propertyName)"
                        ></v-switch>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </v-container>
            </v-tab-item>
            <v-tab-item>
              <v-container fluid>
                <v-card outlined>
                  <v-card-title>{{ $t('return.settings.frontReturnAccess') }}</v-card-title>
                  <v-container>
                    <v-row>
                      <v-col class="text-center" cols="12">
                        {{ $t('return.settings.blockReturnCreation') }}
                      </v-col>
                      <v-col cols="12" lg="6" md="6" sm="6">
                        <validation-provider
                          rules="min_value:1|integer|required"
                          :name="$t('return.settings.delay')"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            hide-details="auto"
                            class="text-uppercase"
                            outlined
                            dense
                            v-model="settingsForm.block_return_creation.delay"
                            type="number"
                            :label="$t('return.settings.delay')"
                            :error-messages="errors"
                            required
                            :disabled="loading"
                          ></v-text-field>
                        </validation-provider>
                      </v-col>
                      <v-col cols="12" lg="6" md="6" sm="6">
                        <validation-provider rules="required" :name="$t('return.settings.duration')" v-slot="errors">
                          <v-select
                            :errors-message="errors"
                            :items="durationItems"
                            outlined
                            dense
                            item-text="text"
                            item-value="value"
                            v-model="settingsForm.block_return_creation.duration"
                            :label="$t('return.settings.duration')"
                          />
                        </validation-provider>
                      </v-col>
                      <v-col class="text-center" cols="12">
                        {{ $t('return.settings.blockAccessReturnFront') }}
                      </v-col>
                      <v-col cols="12" lg="6" sm="6" md="6">
                        <validation-provider
                          rules="min_value:1|integer|required"
                          :name="$t('return.settings.delay')"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            hide-details="auto"
                            class="text-uppercase"
                            outlined
                            dense
                            v-model="settingsForm.block_return_access.delay"
                            type="number"
                            :label="$t('return.settings.delay')"
                            :error-messages="errors"
                            required
                            :disabled="loading"
                          ></v-text-field>
                        </validation-provider>
                      </v-col>
                      <v-col cols="12" lg="6" sm="6" md="6">
                        <validation-provider rules="required" :name="$t('return.settings.duration')" v-slot="errors">
                          <v-select
                            :errors-message="errors"
                            :items="durationItems"
                            outlined
                            dense
                            item-text="text"
                            item-value="value"
                            v-model="settingsForm.block_return_access.duration"
                            :label="$t('return.settings.duration')"
                          />
                        </validation-provider>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>

                <v-card outlined class="mt-2">
                  <v-card-title>{{ $t('return.settings.returnReasonsRequiringValidation') }}</v-card-title>
                  <v-container>
                    <v-row>
                      <v-col>
                        <v-autocomplete
                          v-model="settingsForm.reasons_requiring_validation"
                          dense
                          :items="returnReasons"
                          :label="$t('return.settings.returnReasonsRequiringValidation')"
                          hide-details="auto"
                          multiple
                          :messages="$t('return.settings.returnReasonsRequiringValidationHint')"
                        />
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </v-container>
            </v-tab-item>
            <v-tab-item>
              <v-container fluid>
                <v-card outlined>
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-switch
                          v-model="settingsForm.display.return_label_button"
                          :label="$t('return.settings.displayReturnLabelButton')"
                        />
                      </v-col>

                      <!-- Empty column to force a line break -->
                      <v-col cols="12" sm="6" class="d-none d-sm-block"></v-col>
                      <v-col cols="12">
                        <v-divider></v-divider>
                      </v-col>

                      <v-col cols="12" sm="6">
                        <validation-provider
                          rules="required"
                          :name="$t('return.settings.itemReturnReasons')"
                          v-slot="{ errors }"
                        >
                          <v-autocomplete
                            multiple
                            :items="itemReturnReasons"
                            v-model="settingsForm.display.item_return_reasons"
                            :label="$t('return.settings.itemReturnReasons')"
                            :error-messages="errors"
                            :messages="$t('return.settings.itemReturnReasonsMessage')"
                          >
                            <template v-slot:selection="{ item, index }">
                              <span v-if="index === 0">{{ item.text }}</span>
                              <span v-if="index === 1" class="grey--text caption">
                                (+{{ settingsForm.display.item_return_reasons.length - 1 }} {{ $t('settings.others') }})
                              </span>
                            </template>
                          </v-autocomplete>
                        </validation-provider>
                      </v-col>

                      <v-col cols="12" sm="6">
                        <validation-provider
                          rules="required"
                          :name="$t('return.settings.publicItemReturnReasons')"
                          v-slot="{ errors }"
                        >
                          <v-autocomplete
                            multiple
                            :items="publicItemReturnReasons"
                            v-model="settingsForm.display.public_item_return_reasons"
                            :label="$t('return.settings.publicItemReturnReasons')"
                            :error-messages="errors"
                            :messages="$t('return.settings.publicItemReturnReasonsMessage')"
                          >
                            <template v-slot:selection="{ item, index }">
                              <span v-if="index === 0">{{ item.text }}</span>
                              <span v-if="index === 1" class="grey--text caption">
                                (+{{ settingsForm.display.public_item_return_reasons.length - 1 }}
                                {{ $t('settings.others') }})
                              </span>
                            </template>
                          </v-autocomplete>
                        </validation-provider>
                      </v-col>

                      <v-col cols="12" sm="6">
                        <v-switch
                          v-model="settingsForm.display.item_custom_return_reason"
                          :label="$t('return.settings.displayItemCustomReturnReason')"
                        />
                      </v-col>

                      <!-- Empty column to force a line break -->
                      <v-col cols="12" sm="6" class="d-none d-sm-block"></v-col>

                      <v-col cols="12" sm="6">
                        <validation-provider
                          rules="required"
                          :name="$t('return.settings.returnCancelReasons')"
                          v-slot="{ errors }"
                        >
                          <v-autocomplete
                            multiple
                            :items="returnCancelReasons"
                            v-model="settingsForm.display.return_cancel_reasons"
                            :label="$t('return.settings.returnCancelReasons')"
                            :error-messages="errors"
                            :messages="$t('return.settings.returnCancelReasonsMessage')"
                          >
                            <template v-slot:selection="{ item, index }">
                              <span v-if="index === 0">{{ item.text }}</span>
                              <span v-if="index === 1" class="grey--text caption">
                                (+{{ settingsForm.display.return_cancel_reasons.length - 1 }}
                                {{ $t('settings.others') }})
                              </span>
                            </template>
                          </v-autocomplete>
                        </validation-provider>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <validation-provider
                          rules="required"
                          :name="$t('return.settings.itemNonRefundReasons')"
                          v-slot="{ errors }"
                        >
                          <v-autocomplete
                            multiple
                            :items="itemNonRefundReasons"
                            v-model="settingsForm.display.item_non_refund_reasons"
                            :label="$t('return.settings.itemNonRefundReasons')"
                            :error-messages="errors"
                            :messages="$t('return.settings.itemNonRefundReasonsMessage')"
                          >
                            <template v-slot:selection="{ item, index }">
                              <span v-if="index === 0">{{ item.text }}</span>
                              <span v-if="index === 1" class="grey--text caption">
                                (+{{ settingsForm.display.item_non_refund_reasons.length - 1 }}
                                {{ $t('settings.others') }})
                              </span>
                            </template>
                          </v-autocomplete>
                        </validation-provider>
                      </v-col>

                      <v-col cols="12" sm="6">
                        <validation-provider
                          rules="url|max:2048"
                          mode="eager"
                          :name="$t('return.settings.returnInstructionUrl')"
                          v-slot="{ errors }"
                        >
                          <v-text-field
                            v-model="settingsForm.display.return_instructions_url.fallback"
                            :label="$t('return.settings.returnInstructionUrl')"
                            :placeholder="$t('return.settings.returnInstructionUrlPlaceholder')"
                            :messages="$t('return.settings.returnInstructionUrlMessage')"
                            :error-messages="errors"
                            clearable
                          />
                        </validation-provider>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </v-container>
            </v-tab-item>
            <v-tab-item>
              <v-container fluid>
                <v-card outlined>
                  <v-container fluid>
                    <v-row>
                      <v-col cols="12" sm="6">
                        <v-autocomplete
                          multiple
                          :items="allRoles"
                          v-model="settingsForm.display.refund_menu_roles"
                          :label="$t('return.settings.displayRefundMenuRoles')"
                        >
                          <template v-slot:selection="{ item, index }">
                            <span v-if="index === 0">{{ item.text }}</span>
                            <span v-if="index === 1" class="grey--text caption">
                              (+{{ settingsForm.display.refund_menu_roles.length - 1 }} {{ $t('settings.others') }})
                            </span>
                          </template>
                        </v-autocomplete>
                      </v-col>
                      <v-col cols="12" sm="6">
                        <v-autocomplete
                          multiple
                          :items="allRoles"
                          v-model="settingsForm.permissions.refund_update_roles"
                          :label="$t('return.settings.refundUpdateAuthorizedRoles')"
                        >
                          <template v-slot:selection="{ item, index }">
                            <span v-if="index === 0">{{ item.text }}</span>
                            <span v-if="index === 1" class="grey--text caption">
                              (+{{ settingsForm.permissions.refund_update_roles.length - 1 }} {{ $t('settings.others') }})
                            </span>
                          </template>
                        </v-autocomplete>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-card>
              </v-container>
            </v-tab-item>
          </v-tabs>
          <v-card-actions class="justify-center">
            <v-btn
              :loading="loading"
              :disabled="loading || loadingSettings"
              class="ma-2"
              color="primary"
              outlined
              type="submit"
            >
              {{ $t('return.settings.save') }}
            </v-btn>
            <v-btn
              v-if="!isNewSetting"
              :disabled="loading"
              class="ma-2"
              color="primary"
              outlined
              @click="showDeleteDialog"
            >
              {{ $t('return.settings.delete') }}
            </v-btn>
          </v-card-actions>
        </v-form>
      </validation-observer>
    </v-card>
    <ConfirmationDeleteReturnSettings v-model="deleteDialog" @confirm="confirmDelete" @close="closeDeleteDialog" />
  </v-container>
</template>

<script>
  import { EventBus } from '@/event-bus'
  import { ValidationObserver, ValidationProvider } from 'vee-validate'
  import CountriesAutocomplete from '@/components/CountriesAutocomplete'
  import ConfirmationDeleteReturnSettings from '@/components/dialogs/ConfirmationDeleteReturnSettings'
  import { mapState } from 'vuex'
  const currencies = require('@/assets/currencies.json')

  export default {
    name: 'ReturnSettingsDetails',
    components: {
      ValidationProvider,
      ValidationObserver,
      CountriesAutocomplete,
      ConfirmationDeleteReturnSettings,
    },
    data() {
      return {
        settingsForm: {
          country_code: '',
          pickup_hours: {
            begin: '',
            end: '',
          },
          available_address_picking: false,
          charge_return_fees: {
            return_fees_amount: 1,
            return_fees_currency: undefined,
            available: true,
            display_button: false,
          },
          optional_form_fields: {
            civility: true,
            street_2: true,
            street_3: true,
            company: true,
            district: true,
            phone: true,
            cell_phone: true,
            region: true,
          },
          block_return_creation: {
            delay: 1,
            duration: 'month',
          },
          block_return_access: {
            delay: 6,
            duration: 'month',
          },
          default_settings: false,
          allow_return_creation_in_shop: [],
          cancel_after_receipt: {
            behavior: 'do_not_restore_items',
            ask_confirmation: false,
          },
          refund_on_terminal: {
            activate: false,
            activate_test_mode: false,
            send_return_reference: false,
          },
          display: {
            return_label_button: true,
            item_custom_return_reason: true,
            item_return_reasons: null,
            public_item_return_reasons: null,
            return_cancel_reasons: null,
            item_non_refund_reasons: null,
            return_instructions_url: { fallback: null, localizations: {} },
            refund_menu_roles: [],
          },
          refund_order_carrier_cost: {
            activate: true,
            display_refund_button: true,
          },
          permissions: {
            refund_update_roles: [],
          },
          waiting_products_receipt_timeout: {
            delay: 90,
          },
        },
        menuBegin: false,
        menuEnd: false,
        loading: false,
        loadingSettings: false,
        deleteDialog: false,
        isNewSetting: false,
        vueAppEnv: process.env.VUE_APP_ENV,
        currencies: currencies,
      }
    },
    mounted() {
      if (this.$route.params.id && this.$route.params.id !== 'add') {
        this.loadingSettings = true
        this.getSettings()
      } else {
        this.isNewSetting = true
        this.settingsForm.display.item_return_reasons = this.itemReturnReasons.map(reason => reason.value);
        this.settingsForm.display.public_item_return_reasons = this.publicItemReturnReasons.map(reason => reason.value);
        this.settingsForm.display.return_cancel_reasons = this.returnCancelReasons.map(reason => reason.value);
        this.settingsForm.display.item_non_refund_reasons = this.itemNonRefundReasons.map(reason => reason.value);
      }
    },
    methods: {
      onSubmit: function () {
        if (this.loading || this.loadingSettings) {
          return
        }
        // Trim border whitespace pasted into the URL field before validating,
        // so a leading/trailing space doesn't fail the (whitespace-strict) url rule.
        const instructionsUrl = this.settingsForm.display.return_instructions_url
        if (instructionsUrl && typeof instructionsUrl.fallback === 'string') {
          instructionsUrl.fallback = instructionsUrl.fallback.trim() || null
        }
        this.$refs.form.validate().then((success) => {
          if (success) {
            this.loading = true
            if (this.$route.params.id && this.$route.params.id !== 'add') {
              this.update(this.settingsForm)
            } else {
              this.create(this.settingsForm)
            }
          } else {
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('settings.invalidSettings'),
              color: 'error',
            })
          }
        })
      },
      getSettings() {
        let myInit = this.buildMyInit()
        this.$Amplify.API.get('returns', '/v1/int/settings/' + this.$route.params.id, myInit)
          .then((res) => {
            this.settingsForm = res
            // Older settings may omit the block or one of its independent parameters.
            this.$set(this.settingsForm, 'cancel_after_receipt', {
              behavior: 'do_not_restore_items',
              ask_confirmation: false,
              ...res.cancel_after_receipt,
            })
            // TODO: Remap properties of `res` to apply default values
            if (res.pickup_hours) {
              this.settingsForm.begin = res.pickup_hours.begin
              this.settingsForm.end = res.pickup_hours.end
            }
            // Safety net: the API returns return_instructions_url by default, but if it's
            // ever missing we restore the structure to avoid a render error on `.fallback`.
            if (this.settingsForm.display && !this.settingsForm.display.return_instructions_url) {
              this.$set(this.settingsForm.display, 'return_instructions_url', { fallback: null, localizations: {} })
            }
            if (this.settingsForm.display && !this.settingsForm.display.refund_menu_roles) {
              this.$set(this.settingsForm.display, 'refund_menu_roles', [])
            }
            if (!this.settingsForm.permissions) {
              this.$set(this.settingsForm, 'permissions', { refund_update_roles: [] })
            } else if (!this.settingsForm.permissions.refund_update_roles) {
              this.$set(this.settingsForm.permissions, 'refund_update_roles', [])
            }
            this.settingsForm.waiting_products_receipt_timeout = this.settingsForm.waiting_products_receipt_timeout || {
              delay: 90,
              activate: false
            }
            this.loadingSettings = false
          })
          .catch((e) => {
            this.loadingSettings = false
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('return.settings.errorLoadingSettings'),
              color: 'error',
            })
            this.$router.push({ name: 'returnSettingsTable' })
          })
      },
      create(form) {
        let myInit = this.buildMyInit(form)
        this.$Amplify.API.post('returns', '/v1/int/settings', myInit)
          .then(() => {
            this.loading = false
            this.$refs.form.reset()
            this.$router.push({ name: 'returnSettingsTable' })
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('return.settings.settingsCreated'),
              color: 'success',
            })
          })
          .catch((e) => {
            this.loading = false
            console.log(e.response)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: e?.response?.data?.message || this.$t('return.settings.errorCreatingSettings'),
              color: 'error',
            })
          })
      },
      update(form) {
        let myInit = this.buildMyInit(form)
        this.$Amplify.API.put('returns', '/v1/int/settings/' + this.$route.params.id, myInit)
          .then(() => {
            this.loading = false
            this.$refs.form.reset()
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: this.$t('return.settings.settingsUpdated'),
              color: 'success',
            })
          })
          .catch((e) => {
            this.loading = false
            console.log(e)
            EventBus.$emit('displaySnackbar', {
              display: true,
              text: e?.response?.data?.message || this.$t('return.settings.errorUpdatingSettings'),
              color: 'error',
            })
          })
      },
      allowedStep: (m) => m % 5 === 0,
      buildMyInit(formData = null) {
        let init = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }
        if (formData) {
          init.body = this.removeEmptyValue(formData)
        }

        return init
      },
      showDeleteDialog() {
        this.deleteDialog = true
      },
      closeDeleteDialog() {
        this.deleteDialog = false
      },
      confirmDelete() {
        let myInit = {
          headers: {
            Authorization: 'Bearer ' + this.$auth.accessToken,
          },
        }

        this.$Amplify.API.del('returns', '/v1/int/settings/' + this.$route.params.id, myInit)
          .then(() => {
            this.deleteDialog = false
            this.$router.push({ name: 'returnSettingsTable' })
          })
          .catch((e) => {
            console.log(e)
            this.deleteDialog = false
            EventBus.$emit('displaySnackBar', {
              display: true,
              test: this.$t('return.settings.errorDelete'),
              color: 'error',
            })
          })
      },
      removeEmptyValue(form) {
        let newFormObject = {}
        for (let key of Object.keys(form)) {
          if (form[key] !== '' && form[key] !== null) {
            newFormObject[key] = form[key]
          }
        }

        return newFormObject
      },
    },
    computed: {
      ...mapState(['isOctipasAdmin', 'isAdmin']),
      cancelAfterReceiptBehaviors() {
        return [
          { text: this.$t('return.settings.cancelAfterReceipt.doNotRestoreItems'), value: 'do_not_restore_items' },
          { text: this.$t('return.settings.cancelAfterReceipt.restoreItems'), value: 'restore_items' },
          { text: this.$t('return.settings.cancelAfterReceipt.askUser'), value: 'ask_user' },
        ]
      },
      durationItems() {
        return [
          { text: this.$t('return.settings.days'), value: 'days' },
          { text: this.$t('return.settings.month'), value: 'month' },
        ]
      },
      returnReasons() {
        return [
          { text: this.$t('return.styleDoesNotSuitMe'), value: 'style_does_not_suit_me' },
          { text: this.$t('return.productTooBig'), value: 'product_too_big' },
          { text: this.$t('return.productTooSmall'), value: 'product_too_small' },
          {
            text: this.$t('return.productNotCorrespondingToThePhoto'),
            value: 'product_not_corresponding_to_the_photo',
          },
          { text: this.$t('return.lateDelivery'), value: 'late_delivery' },
          { text: this.$t('return.disappointingQuality'), value: 'disappointing_quality' },
          { text: this.$t('return.severalSizesOrdered'), value: 'several_sizes_ordered' },
          { text: this.$t('return.defectiveProduct'), value: 'defective_product' },
          { text: this.$t('return.customerNoShow'), value: 'customer_no_show' },
          { text: this.$t('return.wrongProductReceived'), value: 'wrong_product_received' },
          // text: this.$t("return.other"), value: "other" },
        ]
      },
      itemReturnReasons() {
        return [
          { text: this.$t('return.styleDoesNotSuitMe'), value: 'style_does_not_suit_me' },
          { text: this.$t('return.productTooBig'), value: 'product_too_big' },
          { text: this.$t('return.productTooSmall'), value: 'product_too_small' },
          {
            text: this.$t('return.productNotCorrespondingToThePhoto'),
            value: 'product_not_corresponding_to_the_photo',
          },
          { text: this.$t('return.lateDelivery'), value: 'late_delivery' },
          { text: this.$t('return.disappointingQuality'), value: 'disappointing_quality' },
          { text: this.$t('return.severalSizesOrdered'), value: 'several_sizes_ordered' },
          { text: this.$t('return.defectiveProduct'), value: 'defective_product' },
          { text: this.$t('return.customerNoShow'), value: 'customer_no_show' },
          { text: this.$t('return.wrongProductReceived'), value: 'wrong_product_received' },
        ]
      },
      publicItemReturnReasons() {
        return [
          { text: this.$t('return.styleDoesNotSuitMe'), value: 'style_does_not_suit_me' },
          { text: this.$t('return.productTooBig'), value: 'product_too_big' },
          { text: this.$t('return.productTooSmall'), value: 'product_too_small' },
          {
            text: this.$t('return.productNotCorrespondingToThePhoto'),
            value: 'product_not_corresponding_to_the_photo',
          },
          { text: this.$t('return.lateDelivery'), value: 'late_delivery' },
          { text: this.$t('return.disappointingQuality'), value: 'disappointing_quality' },
          { text: this.$t('return.severalSizesOrdered'), value: 'several_sizes_ordered' },
          { text: this.$t('return.defectiveProduct'), value: 'defective_product' },
          { text: this.$t('return.wrongProductReceived'), value: 'wrong_product_received' },
        ]
      },
      returnCancelReasons() {
        return [
          { text: this.$t('return.clientChangeOfMind'), value: 'client_changed_his_mind' },
          { text: this.$t('return.deterioratedProducts'), value: 'deteriorated_products' },
          { text: this.$t('return.missingProduct'), value: 'missing_product' },
        ]
      },
      itemNonRefundReasons() {
        return [
          { text: this.$t('return.incompleteProduct'), value: 'incomplete_product' },
          { text: this.$t('return.damagedProduct'), value: 'damaged_product' },
        ]
      },
      itemReturnCreationAuthorizedRoles() {
        return [
          { text: this.$t('users.seller'), value: 'default-user' },
          { text: this.$t('users.shopManager'), value: 'oms-shop-manager' },
        ]
      },
      allRoles() {
        return [
          { text: this.$t('users.seller'), value: 'default-user' },
          { text: this.$t('users.shopManager'), value: 'oms-shop-manager' },
          { text: this.$t('users.customerService'), value: 'oms-customer-service' },
          { text: this.$t('users.admin'), value: 'oms-admin' },
        ]
      },
    },
  }
</script>
